import React, { useContext, useCallback } from 'react';
import { connect } from 'react-redux';

import { AppContext } from 'src/components/app-context';
import { TitleSmallText, FontEmphasis } from '@prisjakt/ui/text/variants';
import { Column } from '@prisjakt/ui/list/variants';

import { AppState } from '../../../reducers';
import { logoutRequest } from '../actions';
import {
  closeLists as closeListsAction,
  selectList as selectListAction,
} from '../../sidebar/actions/lists';
import { User } from '../../../types';

import * as S from './styles';
import { IconAnalytics } from '@prisjakt/ui/icon';
import { Dropdown, DropdownContent } from '@prisjakt/ui/dropdown';
import { ButtonSize, ButtonType } from '@prisjakt/ui/button';
import { Direction } from '@prisjakt/ui';

type OwnProps = {
  style?: {};
  className?: string;
};

type StateProps = {
  me: User;
};

type DispatchProps = {
  dispatchLogoutRequest: (message: string) => void;
  closeLists: () => void;
  selectList: (id: number | null) => void;
};

type Props = OwnProps & DispatchProps & StateProps;

const AccountMenu: React.FC<Props> = ({
  dispatchLogoutRequest,
  style,
  className,
  closeLists,
  selectList,
  me,
}) => {
  const { t } = useContext(AppContext);

  const onLogout = useCallback(() => {
    selectList(null);
    closeLists();
    dispatchLogoutRequest(t('account', 'logout.successful'));
  }, [selectList, closeLists, dispatchLogoutRequest, t]);

  return (
    <Dropdown className={className} style={style} closeOnItemClick>
      <S.DropdownToggle
        onlyIcon
        type={ButtonType.TEXT}
        size={ButtonSize.MEDIUM}
      >
        <S.UserAvatar {...me} size={24} />
      </S.DropdownToggle>
      <DropdownContent width={200} alignment={Direction.Right}>
        <S.ProfileLinks gap={1}>
          <S.MenuItemLink to="/account/profile">
            <TitleSmallText>{t('account', 'profile.title')}</TitleSmallText>
          </S.MenuItemLink>

          <S.MenuItemLink to="/account/profile/settings">
            <TitleSmallText>{t('account', 'menu.settings')}</TitleSmallText>
          </S.MenuItemLink>

          {me?.analyticsAccess && (
            <Column gap={0.5}>
              <TitleSmallText emphasis={FontEmphasis.MEDIUM}>
                <S.AnalyticsWrapper gap={0.25}>
                  <IconAnalytics /> Analytics
                </S.AnalyticsWrapper>
              </TitleSmallText>

              {me.analyticsAccess.stores &&
                me.analyticsAccess.stores?.map(
                  store =>
                    store && (
                      <S.MenuItemLink
                        key={store.name}
                        href={store.url}
                        data-test="MenuItemStore"
                        as="a"
                      >
                        <TitleSmallText>{store.name}</TitleSmallText>
                      </S.MenuItemLink>
                    )
                )}
              {me.analyticsAccess.brands &&
                me.analyticsAccess.brands?.map(
                  brand =>
                    brand && (
                      <S.MenuItemLink
                        key={brand.name}
                        href={brand.url}
                        data-test="MenuItemBrand"
                        as="a"
                      >
                        <TitleSmallText>{brand.name}</TitleSmallText>
                      </S.MenuItemLink>
                    )
                )}
            </Column>
          )}

          <S.Buttons>
            <S.AccountButton fullWidth onClick={onLogout}>
              {t('account', 'menu.logout-button')}
            </S.AccountButton>
          </S.Buttons>
        </S.ProfileLinks>
      </DropdownContent>
    </Dropdown>
  );
};

const mapStateToProps = (state: AppState) => ({
  me: state.areas.account.me,
});

const dispatchActions = {
  dispatchLogoutRequest: logoutRequest,
  closeLists: closeListsAction,
  selectList: selectListAction,
};

export default connect(mapStateToProps, dispatchActions)(AccountMenu);
